import { MessageCodeEnum } from '~/constants/MessageCode';
import { ApiClientError } from '~/error/ApiClientError';
import { AccountRepository } from '~/repository/AccountRepository';

/**
 * パスワード再設定モーダルのロジック
 */
export const usePasswordResetModalLogic = (
  displayId: string,
  emits: {
    (_event: 'confirmed'): void;
    // (_event: 'canceled'): void;
  },
) => {
  const { startLoad, endLoad } = useLoadingStore();
  const { loginUser } = storeToRefs(useLoginUserStore());
  const { getMessage, showClientMessage } = useMessage();

  /** モーダルの開閉状態 */
  const isOpen = ref(false);

  /** パスワード再設定フォーム */
  const passwordResetForm = ref<{
    /** 現在のパスワード */
    currentPassword: string;
    /** 新しいパスワード */
    newPassword: string;
    /** 新しいパスワード（確認用） */
    newPasswordConfirmation: string;
  }>({
    currentPassword: '',
    newPassword: '',
    newPasswordConfirmation: '',
  });

  /** エラー */
  const errors = ref<Partial<Record<keyof typeof passwordResetForm.value | '', string[]>>>({});

  /** ID/氏名 */
  const userIdAndName = computed(() => {
    if (!loginUser.value) {
      return '';
    }

    return `${loginUser.value.platId}/${loginUser.value.userName}`;
  });

  /** 決定ボタン */
  const isConfirmBtnDisabled = computed(() => {
    return (
      passwordResetForm.value.currentPassword.trim() === ''
      || passwordResetForm.value.newPassword.trim() === ''
      || passwordResetForm.value.newPasswordConfirmation.trim() === ''
    );
  });

  /**
   * パスワード再設定処理
   */
  const resetPassword = async () => {
    // フォームのバリデーション
    if (!_validateForm()) {
      return;
    }

    // パスワード再設定API呼出し
    startLoad();
    try {
      const accountRepository = new AccountRepository(displayId);
      try {
        await accountRepository.changePassword({
          currentPassword: passwordResetForm.value.currentPassword,
          newPassword: passwordResetForm.value.newPassword,
        });
      }
      catch (error) {
        if (error instanceof ApiClientError) {
          errors.value[''] = [error.message];
          return;
        }

        throw error;
      }
    }
    finally {
      endLoad();
    }

    // パスワード変更必須フラグを解除
    if (loginUser.value) {
      loginUser.value.mustChangePassword = false;
    }

    // 完了メッセージを表示
    showClientMessage(displayId, MessageCodeEnum.PASSWORD_CHANGED, false, () => {
      // 呼び出し元画面に完了通知
      emits('confirmed');
    });
  };

  /**
   * モーダルを開く
   */
  const open = () => {
    isOpen.value = true;
  };

  /**
   * モーダルを閉じる
   */
  const close = () => {
    isOpen.value = false;

    // フォーム初期化
    passwordResetForm.value.currentPassword = '';
    passwordResetForm.value.newPassword = '';
    passwordResetForm.value.newPasswordConfirmation = '';
    errors.value = {};
  };

  /**
   * フォームのバリデーション
   *
   * @returns バリデーション結果(true: OK, false: NG)
   */
  const _validateForm = (): boolean => {
    errors.value = {};

    // 現在のパスワードの必須チェック
    const currentPasswordRequiredCheckResult = validationUtils.checkRequired(
      passwordResetForm.value.currentPassword,
    );
    if (!currentPasswordRequiredCheckResult.valid) {
      errors.value.currentPassword = [getMessage(currentPasswordRequiredCheckResult.error, ['現在のパスワード'])];
    }
    // 新しいパスワードの必須チェック
    const newPasswordRequiredCheckResult = validationUtils.checkRequired(
      passwordResetForm.value.newPassword,
    );
    if (!newPasswordRequiredCheckResult.valid) {
      errors.value.newPassword = [getMessage(newPasswordRequiredCheckResult.error, ['新しいパスワード'])];
    }
    // 新しいパスワード（確認用）の必須チェック
    const newPasswordConfirmationRequiredCheckResult = validationUtils.checkRequired(
      passwordResetForm.value.newPasswordConfirmation,
    );
    if (!newPasswordConfirmationRequiredCheckResult.valid) {
      errors.value.newPasswordConfirmation = [getMessage(newPasswordConfirmationRequiredCheckResult.error, ['新しいパスワード（確認用）'])];
    }

    // 現在のパスワードと新しいパスワードの不一致チェック
    const currentAndNewPasswordMismatchCheckResult = validationUtils.checkPasswordMismatch(
      passwordResetForm.value.currentPassword,
      passwordResetForm.value.newPassword,
    );
    if (!currentAndNewPasswordMismatchCheckResult.valid) {
      if (errors.value.newPassword === undefined) {
        errors.value.newPassword = [];
      }
      errors.value.newPassword.push(getMessage(currentAndNewPasswordMismatchCheckResult.error));
    }

    // 新しいパスワードのパスワードポリシーチェック
    const newPasswordPolicyCheckResult = validationUtils.checkPasswordPolicy(
      passwordResetForm.value.newPassword,
    );
    if (!newPasswordPolicyCheckResult.valid) {
      if (errors.value.newPassword === undefined) {
        errors.value.newPassword = [];
      }
      errors.value.newPassword.push(getMessage(newPasswordPolicyCheckResult.error));
    }

    // 新しいパスワードと確認用の一致チェック
    const newPasswordMatchCheckResult = validationUtils.checkNewPasswordMatch(
      passwordResetForm.value.newPassword,
      passwordResetForm.value.newPasswordConfirmation,
    );
    if (!newPasswordMatchCheckResult.valid) {
      if (errors.value.newPasswordConfirmation === undefined) {
        errors.value.newPasswordConfirmation = [];
      }
      errors.value.newPasswordConfirmation.push(getMessage(newPasswordMatchCheckResult.error));
    }

    return Object.keys(errors.value).length === 0;
  };

  return {
    isOpen,
    passwordResetForm,
    errors,
    userIdAndName,
    isConfirmBtnDisabled,
    resetPassword,
    open,
    close,
  };
};
